import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { lastPathInWorkspace } from "../../../../App";
import Files from "../../../API/Files";
import workspacesHelper from "../../../API/Workspaces/workspacesHelper";
import Constants from "../../../Constants";
import useWorkspaces from "../../../Hooks/useWorkspaces";
import { useHistory } from "../../../Router";
import { EntityAI } from "../../../models";
import WorkspaceModalContent from "../../Workspaces/Components/WorkspaceModalContent";
import { formValidationSchema } from "../../Workspaces/WorkspacesScreenContainer";
import entitiesHelper from "../../../API/EntitiesAI/entitiesHelper";
import {
  normalizeBlockedModels,
  blockedModelsToIds,
} from "../../../Utils/modelRestrictions";

const WorkspaceSettings = ({ readOnly }) => {
  const { t } = useTranslation();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { user } = AppUser.useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [targetPlan, setTargetPlan] = useState(false);
  const [workspaceLogoFilename, setWorkspaceLogoFilename] = useState(null);
  const [workspaceLogo, setWorkspaceLogo] = useState(null);
  const [fileUploadInProgress, setFileUploadInProgress] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const {
    getActiveWorkspace,
    generateBaseWorkspaceTrainingConfig,
    canUpdateSubscription,
  } = useWorkspaces();
  const [selectedWorkspace, setSelectedWorkspace] = useState();
  const [modalContentKey, setModalContentKey] = useState("UPDATE");
  const history = useHistory();
  const [initialValues, setInitialValues] = useState(
    Constants.initialWorkspaceValues,
  );

  const AppToasty = AppToast.useToast();
  const identifyWorkspaceByID = (id) => {
    return state.workspaces.map((u) => u.id).indexOf(id);
  };

  const loadWorkspaceLogo = async (workspaceId, logoFilename) => {
    if (!logoFilename || !workspaceId) {
      setWorkspaceLogo(null);
      return;
    }
    try {
      const _url = await Files.downloadUrlGeneration({
        filename: `imported-image/${workspaceId}/${logoFilename}`,
        context: "pdf",
        workspaceid: workspaceId,
      });
      const logoUrl = _url?.data?.url || _url?.url;
      if (logoUrl) {
        setWorkspaceLogo(logoUrl);
      }
    } catch (error) {
      console.log("Error loading workspace logo:", error);
      setWorkspaceLogo(null);
    }
  };

  const handleValuesChange = (values) => {};
  useEffect(() => {
    const workspace = getActiveWorkspace();
    if (state?.activeWorkspaceID && workspace?.title) {
      handleWorkspaceEdit(workspace);
    }
  }, [
    state?.activeWorkspaceID,
    getActiveWorkspace()?.title,
    getActiveWorkspace()?.logo,
  ]);

  const cancelPlanChange = async () => {
    const payload = [
      {
        property: "targetSubscriptionType",
        value: null,
      },
    ];
    const result = await workspacesHelper.updateWorkspace(
      selectedWorkspace.id,
      payload,
    );
    state.workspaces[identifyWorkspaceByID(result.id)] = result;
    setInitialValues({
      ...initialValues,
      targetSubscriptionType: selectedWorkspace.subscriptionType,
      baseModel: selectedWorkspace?.baseModel?.toUpperCase() || "NONE",
      domain: selectedWorkspace?.domain?.toUpperCase() || "DEFAULT",
      defaultChatVisibility:
        selectedWorkspace?.defaultChatVisibility || "private",
      defaultChatAgent: selectedWorkspace?.defaultChatAgent || null,
      isMFAEnabled: selectedWorkspace?.isMFAEnabled || false,
      enforceRegionalModels: selectedWorkspace?.enforceRegionalModels || false,
      blockedModels: normalizeBlockedModels(selectedWorkspace?.blockedModels),
      enableAutoInvite: selectedWorkspace?.enableAutoInvite || false,
      autoInviteDomain: selectedWorkspace?.autoInviteDomain || null,
      autoInviteUserRole: selectedWorkspace?.autoInviteUserRole || null,
    });

    AppToasty.show(t("WorkspaceChangeOfPlanAbort"), { type: "success" });
    window.location.reload();
  };

  const handleWorkspaceEdit = (item) => {
    if (item) {
      setSelectedWorkspace(item);
      setWorkspaceLogoFilename(item?.logo || null);
      if (item?.logo) {
        loadWorkspaceLogo(item.id, item.logo);
      } else {
        setWorkspaceLogo(null);
      }
      setInitialValues({
        workspaceID: item.id,
        workspaceName: item.title,
        workspaceType: item.type,
        workspaceMed: item.isMed ? true : false,
        subscriptionType: item.subscriptionType,
        targetSubscriptionType: item.targetSubscriptionType,
        maxUsers: item.maxUsers,
        parentWorkspaceID: item?.parentWorkspaceID || null,
        baseModel: item?.baseModel?.toUpperCase() || "NONE",
        domain: item?.domain?.toUpperCase() || "DEFAULT",
        defaultChatVisibility: item?.defaultChatVisibility || "private",
        defaultChatAgent: item?.defaultChatAgent || null,
        isMFAEnabled: item?.isMFAEnabled || false,
        enforceRegionalModels: item?.enforceRegionalModels || false,
        blockedModels: normalizeBlockedModels(item?.blockedModels),
        enableAutoInvite: item?.enableAutoInvite || false,
        autoInviteDomain: item?.autoInviteDomain || null,
        autoInviteUserRole: item?.autoInviteUserRole || null,
        logo: item?.logo || null,
      });
      setModalContentKey("UPDATE");
      setIsModalOpen(true);
    }
  };
  const handleWorkspaceDeletion = async () => {
    try {
      const childrenToDelete = (state.workspaces || []).filter(
        (ws) => ws.parentWorkspaceID === selectedWorkspace.id,
      );

      for (const child of childrenToDelete) {
        await workspacesHelper.deleteWorkspace(child.id);
      }

      await workspacesHelper.deleteWorkspace(selectedWorkspace.id);
      setIsModalOpen(false);
      dispatch(
        "SET_WORKSPACES",
        state.workspaces.filter(
          (u) =>
            u.id !== selectedWorkspace.id &&
            u.parentWorkspaceID !== selectedWorkspace.id,
        ),
      );
      lastPathInWorkspace(null);
      AppToasty.show(t("WorkspaceDeletionCaption"), { type: "success" });
    } catch (error) {
      AppToasty.show(t("WorkspaceDeletionFailedCaption"), { type: "danger" });
      console.error("Error while deleting the workspace", error);
    }
  };

  const handleFormSubmit = async (values) => {
    let result;
    try {
      if (modalContentKey === "NEW") {
        result = await workspacesHelper.createWorkspace({
          title: values.workspaceName,
          isMed: values.workspaceMed === "true",
          userID: user.id,
          visibility: "shared",
          type: values.workspaceType,
          users: [user.id],
          subscriptionType: values.subscriptionType,
          baseModel: !values.baseModel ? "none" : values.baseModel,
          trainingConfig: generateBaseWorkspaceTrainingConfig(
            values.subscriptionType,
          ),
        });

        const resultAIEntityNER = await Promise.all(
          AppUtilities.baseEntities.map((ner) =>
            entitiesHelper.createEntity({
              label: ner.label,
              description: ner.description,
              backgroundColor: ner.backgroundColor,
              type: "ner",
              workspaceID: result.id,
              emoji: ner.icon,
            }),
          ),
        );

        if (values.workspaceMed) {
          const medresultAIEntityNER = await Promise.all(
            AppUtilities.medEntities.map((ner) =>
              entitiesHelper.createEntity({
                label: ner.label,
                description: ner.description,
                backgroundColor: ner.backgroundColor,
                type: "ner",
                workspaceID: result.id,
                emoji: ner.icon,
              }),
            ),
          );
        }

        const resultAIEntityIntent = await Promise.all(
          Constants.generateBaseIntentsFromBaseModel(
            !values.baseModel ? "none" : values.baseModel,
          )?.map((intent) =>
            entitiesHelper.createEntity({
              label: intent.label,
              backgroundColor: intent.backgroundColor,
              type: "intent",
              workspaceID: result.id,
            }),
          ),
        );

        setIsModalOpen(false);
        dispatch("SET_WORKSPACES", [...state.workspaces, result]);
        AppToasty.show(t("WorkspaceCreationCaption"), { type: "success" });
      } else if (modalContentKey === "UPDATE") {
        const payload = [
          {
            property: "title",
            value: values.workspaceName,
          },
          {
            property: "isMed",
            value: values.workspaceMed,
          },
          {
            property: "domain",
            value: !values.domain ? "default" : values.domain.toLowerCase(),
          },
          {
            property: "defaultChatVisibility",
            value: values.defaultChatVisibility || "private",
          },
          {
            property: "defaultChatAgent",
            value: values.defaultChatAgent || "tf_main",
          },
          {
            property: "isMFAEnabled",
            value: values.isMFAEnabled || false,
          },
          {
            property: "enforceRegionalModels",
            value: values.enforceRegionalModels || false,
          },
          {
            property: "blockedModels",
            value: JSON.stringify(blockedModelsToIds(values.blockedModels)),
          },
          {
            property: "enableAutoInvite",
            value: values.enableAutoInvite || false,
          },
          {
            property: "autoInviteDomain",
            value: values.autoInviteDomain || null,
          },
          {
            property: "autoInviteUserRole",
            value: values.autoInviteUserRole || null,
          },
          {
            property: canUpdateSubscription()
              ? "subscriptionType"
              : "targetSubscriptionType",
            value: values.subscriptionType,
          },
          {
            property: "maxUsers",
            value: values.maxUsers ?? selectedWorkspace?.maxUsers ?? 1,
          },
          {
            property: "logo",
            value: workspaceLogoFilename || values?.logo || null,
          },
        ];
        let reloadRequired = false;
        if (
          values.subscriptionType !== getActiveWorkspace()?.subscriptionType
        ) {
          reloadRequired = true;
          payload.push({
            property: "lastChangeSubscriptionDate",
            value: new Date().toISOString(),
          });
        }

        result = await workspacesHelper.updateWorkspace(
          selectedWorkspace.id,
          payload,
        );
        state.workspaces[identifyWorkspaceByID(result.id)] = result;
        setIsModalOpen(false);
        dispatch("SET_WORKSPACES", state.workspaces);
        AppToasty.show(t("WorkspaceUpdateCaption"), { type: "success" });
        if (reloadRequired) {
          window.location.reload();
        }
      }
    } catch (error) {
      AppToasty.show(t("WorkspaceUpdateFailedCaption"), { type: "danger" });
      console.error("Error while saving the workspace", error);
    }
  };
  return (
    <WorkspaceModalContent
      cancelPlanChange={cancelPlanChange}
      modalContentKey={modalContentKey}
      handleFormSubmit={handleFormSubmit}
      formValidationSchema={formValidationSchema}
      initialValues={initialValues}
      setModalContentKey={setModalContentKey}
      setIsModalOpen={setIsModalOpen}
      handleWorkspaceDeletion={handleWorkspaceDeletion}
      setTargetPlan={setTargetPlan}
      targetPlan={targetPlan}
      onValuesChange={handleValuesChange}
      workspaceLogo={workspaceLogo}
      setWorkspaceLogo={setWorkspaceLogo}
      fileUploadInProgress={fileUploadInProgress}
      setFileUploadInProgress={setFileUploadInProgress}
      fileUploadProgress={fileUploadProgress}
      setFileUploadProgress={setFileUploadProgress}
      setWorkspaceLogoFilename={setWorkspaceLogoFilename}
      selectedWorkspace={selectedWorkspace}
      childWorkspacesCount={
        (state.workspaces || []).filter(
          (ws) => ws.parentWorkspaceID === selectedWorkspace?.id,
        ).length
      }
    />
  );
};

export default WorkspaceSettings;
